import { useEffect, useState, type ReactNode } from "react";
import { useNav } from "../context/NavContext";
import { Logo, SectionLabel } from "../components/ui";
import { Ic } from "../components/icons";
import { openCookieSettings } from "../lib/consent";

/* Page légale qui regroupe les 5 documents obligatoires :
 * mentions légales (LCEN), politique de confidentialité (RGPD Art. 13-14),
 * politique cookies (ePrivacy/CNIL), CGU, CGV.
 *
 * Navigation locale par tabs + synchro URL hash (#confidentialite, #cgu…) :
 * ça permet de linker directement vers une section depuis le footer ou un
 * email RGPD, sans dépendre d'un vrai router côté app.
 *
 * Header minimal self-contained : volontaire — la migration Brand (en cours
 * sur main) introduit un <PublicHeader /> partagé qu'on branchera ici dans
 * un commit ultérieur, une fois la migration mergée.
 */

type Section = "mentions" | "confidentialite" | "cookies" | "cgu" | "cgv";

const SECTIONS: { id: Section; label: string; hash: string }[] = [
  { id: "mentions", label: "Mentions légales", hash: "mentions-legales" },
  { id: "confidentialite", label: "Confidentialité", hash: "confidentialite" },
  { id: "cookies", label: "Cookies", hash: "cookies" },
  { id: "cgu", label: "CGU", hash: "cgu" },
  { id: "cgv", label: "CGV", hash: "cgv" },
];

const LAST_UPDATE = "24 avril 2026";

export default function Legal() {
  const { go } = useNav();
  const [active, setActive] = useState<Section>("mentions");

  // Sync initial + hashchange → permet de partager une URL du type
  // arpentia.fr/#confidentialite qui ouvre directement la bonne section.
  useEffect(() => {
    function sync() {
      const hash = window.location.hash.slice(1);
      const found = SECTIONS.find((s) => s.hash === hash);
      if (found) setActive(found.id);
    }
    sync();
    window.addEventListener("hashchange", sync);
    return () => window.removeEventListener("hashchange", sync);
  }, []);

  function goSection(id: Section) {
    setActive(id);
    const s = SECTIONS.find((x) => x.id === id)!;
    window.history.replaceState(null, "", `#${s.hash}`);
    window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
  }

  return (
    <div className="min-h-screen bg-canvas">
      {/* Header minimal (self-contained, cf. commentaire en tête de fichier) */}
      <header className="sticky top-0 z-30 backdrop-blur bg-canvas/85 border-b border-ink-200/40">
        <div className="max-w-[1040px] mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between gap-4">
          <button
            onClick={() => go("landing")}
            aria-label="Retour à l'accueil"
            className="cursor-pointer shrink-0"
          >
            <Logo />
          </button>
          <button
            onClick={() => go("landing")}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-[13px] text-ink-700 hover:text-ink-900 hover:bg-ink-100 transition"
          >
            <Ic.Arrow size={13} className="rotate-180" />
            Retour à l'accueil
          </button>
        </div>
      </header>

      {/* Titre */}
      <section className="max-w-[1040px] mx-auto px-4 sm:px-6 pt-10 sm:pt-14 pb-6">
        <SectionLabel>Informations légales</SectionLabel>
        <h1 className="mt-3 text-[28px] sm:text-[40px] leading-tight tracking-tight font-semibold text-ink-900">
          Mentions, CGU, CGV et{" "}
          <span className="font-serif italic font-normal">confidentialité</span>
        </h1>
        <p className="mt-3 text-[15px] text-ink-600 max-w-[640px] leading-relaxed">
          Toutes les informations légales et contractuelles encadrant
          l'utilisation d'Arpentia. En cas de question, contactez-nous à{" "}
          <span className="font-mono text-[13px] text-ink-800">
            privacy@arpentia.fr
          </span>
          .
        </p>
      </section>

      {/* Tab bar sticky sous le header */}
      <div className="sticky top-14 sm:top-16 z-20 bg-canvas/85 backdrop-blur border-b border-ink-200/40">
        <div className="max-w-[1040px] mx-auto px-4 sm:px-6 py-3 flex items-center gap-1 overflow-x-auto">
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              onClick={() => goSection(s.id)}
              aria-current={active === s.id ? "page" : undefined}
              className={`px-3 h-9 rounded-full text-[13px] whitespace-nowrap transition ${
                active === s.id
                  ? "bg-ink-900 text-white font-medium"
                  : "text-ink-700 hover:bg-ink-100"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Contenu */}
      <main className="max-w-[760px] mx-auto px-4 sm:px-6 py-10 sm:py-14">
        {active === "mentions" && <MentionsLegales />}
        {active === "confidentialite" && <Confidentialite />}
        {active === "cookies" && <Cookies />}
        {active === "cgu" && <CGU />}
        {active === "cgv" && <CGV />}

        {/* Nav inter-sections au pied */}
        <div className="mt-14 pt-8 border-t border-ink-200/70 flex flex-wrap items-center justify-between gap-4">
          <div className="font-mono text-[11px] uppercase tracking-[0.14em] text-ink-500">
            Dernière mise à jour · {LAST_UPDATE}
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[12.5px]">
            {SECTIONS.filter((s) => s.id !== active).map((s) => (
              <button
                key={s.id}
                onClick={() => goSection(s.id)}
                className="text-ink-600 hover:text-ink-900 underline decoration-ink-300 underline-offset-2"
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </main>

      <footer className="border-t border-ink-200/60">
        <div className="max-w-[1040px] mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4 text-[12px] text-ink-500 flex-wrap">
          <span>© 2026 Arpentia SAS — 8 rue d'Uzès, 75002 Paris.</span>
          <div className="flex items-center gap-4 sm:gap-5 flex-wrap">
            <button
              onClick={openCookieSettings}
              className="hover:text-ink-900 transition"
            >
              Gérer mes cookies
            </button>
            <button
              onClick={() => go("landing")}
              className="hover:text-ink-900 transition"
            >
              Retour à l'accueil
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}

/* ---------- Helpers de mise en forme ---------- */

function H2({ children }: { children: ReactNode }) {
  return (
    <h2 className="mt-10 mb-3 text-[20px] sm:text-[22px] font-semibold tracking-tight text-ink-900 first:mt-0">
      {children}
    </h2>
  );
}

function H3({ children }: { children: ReactNode }) {
  return (
    <h3 className="mt-6 mb-2 text-[15px] font-semibold text-ink-900">
      {children}
    </h3>
  );
}

function P({ children }: { children: ReactNode }) {
  return (
    <p className="mt-3 text-[14.5px] text-ink-700 leading-relaxed">
      {children}
    </p>
  );
}

function UL({ children }: { children: ReactNode }) {
  return (
    <ul className="mt-3 ml-5 list-disc space-y-1.5 text-[14.5px] text-ink-700 leading-relaxed marker:text-ink-400">
      {children}
    </ul>
  );
}

function TODO({ children }: { children: ReactNode }) {
  // Marqueur visuel pour les infos à compléter (SIREN, capital, DPO, etc.).
  // Volontairement voyant pour qu'on ne publie pas en prod sans remplir.
  return (
    <span className="inline-block px-1.5 py-0.5 rounded bg-amber-100 text-amber-900 font-mono text-[11.5px]">
      [à compléter&nbsp;: {children}]
    </span>
  );
}

function Table({
  headers,
  rows,
}: {
  headers: string[];
  rows: (ReactNode | string)[][];
}) {
  return (
    <div className="mt-4 overflow-x-auto hairline rounded-lg">
      <table className="w-full text-[13px] text-left">
        <thead className="bg-ink-50">
          <tr>
            {headers.map((h, i) => (
              <th
                key={i}
                className="px-3 py-2 font-medium text-ink-700 border-b border-ink-200/70"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr
              key={ri}
              className={ri % 2 === 0 ? "bg-paper" : "bg-canvas/50"}
            >
              {row.map((cell, ci) => (
                <td
                  key={ci}
                  className="px-3 py-2 align-top text-ink-700 border-b border-ink-200/40"
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ==========================================================================
 * 1 · Mentions légales (LCEN 2004-575)
 * ========================================================================== */

function MentionsLegales() {
  return (
    <article>
      <H2>Mentions légales</H2>
      <P>
        Conformément aux dispositions de la loi n° 2004-575 du 21 juin 2004
        pour la confiance dans l'économie numérique (« LCEN »), les présentes
        mentions légales informent les utilisateurs du site Arpentia de
        l'identité de ses intervenants.
      </P>

      <H3>Éditeur du site</H3>
      <UL>
        <li>
          <strong>Arpentia SAS</strong>, société par actions simplifiée au
          capital de <TODO>montant du capital</TODO>.
        </li>
        <li>Siège social : 8 rue d'Uzès, 75002 Paris, France.</li>
        <li>
          SIREN : <TODO>SIREN</TODO> · RCS Paris : <TODO>RCS</TODO>.
        </li>
        <li>
          N° TVA intracommunautaire : <TODO>TVA</TODO>.
        </li>
        <li>
          Directeur de la publication : <TODO>nom du directeur</TODO>.
        </li>
        <li>
          Contact :{" "}
          <span className="font-mono text-[13px]">contact@arpentia.fr</span>.
        </li>
      </UL>

      <H3>Hébergeur</H3>
      <P>
        Le site est hébergé par Google Cloud EMEA Limited (services Firebase),
        dont le siège est situé Gordon House, Barrow Street, Dublin 4,
        Irlande. Tél. : +353&nbsp;1&nbsp;543&nbsp;1000.
      </P>

      <H3>Propriété intellectuelle</H3>
      <P>
        L'ensemble des contenus présents sur le site (textes, visuels, marques,
        logos, code source) sont protégés au titre du droit d'auteur, du droit
        des marques et du droit des bases de données. Toute reproduction,
        représentation, modification, publication, adaptation ou exploitation,
        totale ou partielle, par quelque procédé que ce soit, est interdite
        sans l'autorisation écrite préalable d'Arpentia SAS.
      </P>

      <H3>Signalement de contenus illicites</H3>
      <P>
        Conformément à l'article 6 de la LCEN, tout contenu manifestement
        illicite peut être signalé à l'adresse{" "}
        <span className="font-mono text-[13px]">abuse@arpentia.fr</span>.
      </P>
    </article>
  );
}

/* ==========================================================================
 * 2 · Politique de confidentialité (RGPD Art. 13-14)
 * ========================================================================== */

function Confidentialite() {
  return (
    <article>
      <H2>Politique de confidentialité</H2>
      <P>
        Arpentia SAS accorde une importance particulière à la protection des
        données personnelles de ses utilisateurs. La présente politique décrit
        les traitements mis en œuvre dans le cadre du service Arpentia,
        conformément au Règlement (UE) 2016/679 (« RGPD ») et à la loi
        Informatique et Libertés modifiée.
      </P>

      <H3>Responsable de traitement</H3>
      <P>
        Arpentia SAS, 8 rue d'Uzès, 75002 Paris. Pour toute question relative
        à vos données personnelles, contactez-nous à{" "}
        <span className="font-mono text-[13px]">privacy@arpentia.fr</span>.
      </P>

      <H3>Données collectées, finalités et bases légales</H3>
      <Table
        headers={["Finalité", "Données", "Base légale", "Durée"]}
        rows={[
          [
            "Création & gestion du compte",
            "email, nom, identifiant unique",
            "Exécution du contrat",
            "Compte actif + 3 ans",
          ],
          [
            "Génération de fiches d'analyse",
            "adresses saisies, résultats d'analyse PLU/cadastre/DVF",
            "Exécution du contrat",
            "Durée de l'abonnement + 3 ans",
          ],
          [
            "Facturation et obligations comptables",
            "email, moyen de paiement (via Stripe), historique de factures",
            "Obligation légale (art. L. 123-22 C. com.)",
            "10 ans",
          ],
          [
            "Mesure d'audience",
            "pages consultées, device, IP tronquée",
            "Consentement",
            "13 mois max",
          ],
          [
            "Anti-fraude & sécurité",
            "fingerprint reCAPTCHA, logs techniques",
            "Intérêt légitime",
            "13 mois",
          ],
          [
            "Support utilisateur",
            "email, contenu des échanges",
            "Intérêt légitime",
            "3 ans après dernier contact",
          ],
        ]}
      />

      <H3>Destinataires et sous-traitants</H3>
      <P>
        Vos données sont traitées par les sous-traitants suivants, qui
        interviennent sur instructions documentées d'Arpentia :
      </P>
      <UL>
        <li>
          <strong>Google Cloud EMEA Limited</strong> (Firebase Auth, Firestore
          europe-west1, Cloud Functions, Firebase Analytics, App Check) —
          hébergement, authentification, stockage, mesure d'audience.
          Transferts UE + États-Unis, encadrés par le Data Privacy Framework.
        </li>
        <li>
          <strong>Anthropic PBC</strong> (API Claude) — analyse IA des
          règlements d'urbanisme. Transfert États-Unis (DPF).
        </li>
        <li>
          <strong>Stripe Payments Europe Ltd</strong> — traitement des
          paiements par carte. Dublin, Irlande.
        </li>
        <li>
          <strong>Google reCAPTCHA</strong> — protection anti-fraude (App
          Check). Transfert États-Unis (DPF).
        </li>
      </UL>
      <P>
        Aucune donnée n'est cédée ni vendue à des tiers à des fins
        commerciales.
      </P>

      <H3>Transferts hors Union européenne</H3>
      <P>
        Certains sous-traitants (Google, Anthropic) sont établis aux
        États-Unis. Ces transferts sont encadrés par le Data Privacy Framework
        (décision d'adéquation de la Commission européenne du 10 juillet 2023).
        Les certifications sont consultables sur{" "}
        <a
          href="https://www.dataprivacyframework.gov"
          target="_blank"
          rel="noopener noreferrer"
          className="underline decoration-ink-300 underline-offset-2 hover:text-ink-900"
        >
          dataprivacyframework.gov
        </a>
        .
      </P>

      <H3>Vos droits</H3>
      <P>
        Conformément aux articles 15 à 22 du RGPD, vous disposez des droits
        suivants :
      </P>
      <UL>
        <li>
          <strong>Accès</strong> (Art. 15) : obtenir une copie des données que
          nous détenons.
        </li>
        <li>
          <strong>Rectification</strong> (Art. 16) : corriger des données
          inexactes.
        </li>
        <li>
          <strong>Effacement</strong> (Art. 17) : demander la suppression de
          votre compte et de vos données, sous réserve des obligations légales
          de conservation (factures 10 ans).
        </li>
        <li>
          <strong>Limitation</strong> (Art. 18) : restreindre temporairement un
          traitement contesté.
        </li>
        <li>
          <strong>Portabilité</strong> (Art. 20) : recevoir vos données dans un
          format structuré et lisible.
        </li>
        <li>
          <strong>Opposition</strong> (Art. 21) : vous opposer à un traitement
          fondé sur l'intérêt légitime.
        </li>
        <li>
          <strong>Retrait du consentement</strong> à tout moment (cookies de
          mesure d'audience notamment).
        </li>
      </UL>
      <P>
        Pour exercer vos droits : écrivez-nous à{" "}
        <span className="font-mono text-[13px]">privacy@arpentia.fr</span>.
        Nous répondons sous un mois maximum. Un export / une suppression de
        compte en libre-service seront disponibles depuis vos Paramètres
        (fonctionnalité à venir, sprint 3 du chantier RGPD).
      </P>

      <H3>Réclamation auprès de la CNIL</H3>
      <P>
        Si vous estimez que vos droits ne sont pas respectés, vous pouvez
        adresser une réclamation à la Commission Nationale de l'Informatique
        et des Libertés :{" "}
        <a
          href="https://www.cnil.fr/fr/plaintes"
          target="_blank"
          rel="noopener noreferrer"
          className="underline decoration-ink-300 underline-offset-2 hover:text-ink-900"
        >
          cnil.fr/fr/plaintes
        </a>
        .
      </P>

      <H3>Sécurité</H3>
      <P>
        Les données sont chiffrées en transit (HTTPS/TLS) et au repos
        (chiffrement Firebase natif). L'accès aux bases est restreint par des
        règles de sécurité Firestore et par l'App Check de Firebase. Les
        sauvegardes sont opérées par Google Cloud.
      </P>

      <H3>Cookies</H3>
      <P>
        La gestion des cookies et traceurs est décrite dans notre{" "}
        <span className="font-medium text-ink-900">Politique cookies</span>.
      </P>
    </article>
  );
}

/* ==========================================================================
 * 3 · Politique cookies (CNIL / ePrivacy)
 * ========================================================================== */

function Cookies() {
  return (
    <article>
      <H2>Politique cookies</H2>
      <P>
        Un cookie (ou traceur) est un petit fichier déposé sur votre terminal
        lors de la consultation d'un site. Certains sont indispensables au
        fonctionnement du service ; d'autres, facultatifs, ne sont déposés
        qu'avec votre consentement.
      </P>

      <H3>Cookies strictement nécessaires</H3>
      <P>
        Ces cookies sont indispensables au fonctionnement du service et ne
        requièrent pas votre consentement (art. 82 loi Informatique et
        Libertés).
      </P>
      <Table
        headers={["Cookie", "Finalité", "Émetteur", "Durée"]}
        rows={[
          [
            "firebase:authUser:*",
            "Maintenir votre session authentifiée",
            "Firebase (Google)",
            "Session",
          ],
          [
            "__Secure-App-Check-*",
            "Protection anti-abus des API (App Check)",
            "Firebase / reCAPTCHA",
            "Session",
          ],
        ]}
      />

      <H3>Cookies de mesure d'audience (consentement requis)</H3>
      <P>
        Ces cookies ne sont déposés qu'après votre consentement explicite via
        la bannière cookies.
      </P>
      <Table
        headers={["Cookie", "Finalité", "Émetteur", "Durée"]}
        rows={[
          [
            "_ga, _ga_*",
            "Mesure d'audience agrégée (pages vues, sessions)",
            "Google Analytics 4 (DPF)",
            "13 mois",
          ],
        ]}
      />

      <H3>Cookies marketing</H3>
      <P>
        À ce jour, Arpentia n'utilise <strong>aucun cookie publicitaire</strong>{" "}
        ni pixel de reciblage (Meta, LinkedIn, etc.).
      </P>

      <H3>Gérer votre consentement</H3>
      <P>
        Vous pourrez accepter, refuser ou personnaliser votre consentement à
        tout moment via le lien{" "}
        <span className="font-medium text-ink-900">« Gérer mes cookies »</span>{" "}
        (module en cours de déploiement — sprint 2 du chantier RGPD). Votre
        consentement est conservé pendant 6 mois ; passé ce délai, nous vous
        le redemandons.
      </P>
      <P>
        Vous pouvez également configurer votre navigateur pour bloquer ou
        supprimer les cookies. Attention : bloquer les cookies essentiels peut
        empêcher le service de fonctionner correctement (connexion impossible).
      </P>
    </article>
  );
}

/* ==========================================================================
 * 4 · Conditions Générales d'Utilisation (CGU)
 * ========================================================================== */

function CGU() {
  return (
    <article>
      <H2>Conditions Générales d'Utilisation</H2>
      <P>
        Les présentes Conditions Générales d'Utilisation (« CGU ») régissent
        l'accès et l'utilisation du service Arpentia, plateforme en ligne
        d'analyse du potentiel immobilier et urbanistique de biens (le
        « Service »).
      </P>

      <H3>1. Acceptation</H3>
      <P>
        La création d'un compte et l'utilisation du Service valent acceptation
        pleine et entière des présentes CGU ainsi que des CGV applicables aux
        abonnements payants. Les CGU peuvent être modifiées ; les utilisateurs
        sont informés par email ou au sein du Service en cas de modification
        substantielle.
      </P>

      <H3>2. Accès au Service</H3>
      <P>
        L'accès nécessite la création d'un compte (email + mot de passe ou
        authentification Google). L'utilisateur s'engage à fournir des
        informations exactes et à maintenir la confidentialité de ses
        identifiants. Arpentia se réserve le droit de suspendre tout compte
        présentant un risque manifeste (usage frauduleux, partage
        d'identifiants, automatisation non autorisée).
      </P>

      <H3>3. Obligations de l'utilisateur</H3>
      <UL>
        <li>
          Utiliser le Service conformément à la loi, aux présentes CGU et à sa
          destination professionnelle.
        </li>
        <li>
          Ne pas tenter d'accéder frauduleusement au Service, d'en perturber
          le fonctionnement, ou d'en extraire massivement les données.
        </li>
        <li>
          <strong>Respecter les droits des tiers</strong>, notamment en matière
          de données personnelles. Lorsque l'utilisateur saisit l'adresse d'un
          bien appartenant à un tiers identifié (propriétaire, occupant),
          l'utilisateur agit en qualité de responsable de traitement
          indépendant et s'engage à respecter ses propres obligations au titre
          du RGPD (information de la personne concernée, base légale adaptée).
        </li>
        <li>
          Ne pas diffuser publiquement les fiches générées sans anonymisation
          préalable des éléments permettant d'identifier les personnes
          concernées.
        </li>
      </UL>

      <H3>4. Nature des analyses et limitation de responsabilité</H3>
      <P>
        Les fiches et analyses produites par Arpentia sont fondées sur des
        données publiques (PLU, cadastre, DVF, Géoportail de l'urbanisme) et
        sur un traitement automatisé par intelligence artificielle. Elles sont
        fournies <strong>à titre indicatif</strong> et ne constituent ni un
        conseil juridique, ni une étude de faisabilité opposable, ni une
        garantie de constructibilité. L'utilisateur demeure seul responsable
        des décisions commerciales et des représentations faites à ses clients
        sur la base de ces fiches. Arpentia n'assure aucune garantie quant à
        l'exhaustivité, l'exactitude ou la mise à jour des données publiques
        qu'elle exploite.
      </P>

      <H3>5. Propriété intellectuelle</H3>
      <P>
        L'intégralité du Service et des contenus éditoriaux qui y sont diffusés
        sont la propriété exclusive d'Arpentia ou de ses partenaires.
        L'utilisateur dispose d'un droit d'usage personnel et non exclusif des
        fiches générées via son abonnement, y compris à des fins commerciales
        dans le cadre de son activité immobilière (remise à des clients,
        intégration à des mandats, etc.).
      </P>

      <H3>6. Données personnelles</H3>
      <P>
        Le traitement des données personnelles de l'utilisateur est décrit
        dans la <span className="font-medium text-ink-900">Politique de confidentialité</span>.
      </P>

      <H3>7. Résiliation</H3>
      <P>
        L'utilisateur peut fermer son compte à tout moment depuis ses
        paramètres. Arpentia peut suspendre ou résilier un compte en cas de
        violation manifeste des présentes CGU, après notification préalable
        sauf urgence caractérisée.
      </P>

      <H3>8. Droit applicable et juridiction</H3>
      <P>
        Les présentes CGU sont régies par le droit français. À défaut de
        résolution amiable, tout litige relève des tribunaux compétents du
        ressort du siège social d'Arpentia.
      </P>
    </article>
  );
}

/* ==========================================================================
 * 5 · Conditions Générales de Vente (CGV)
 * ========================================================================== */

function CGV() {
  return (
    <article>
      <H2>Conditions Générales de Vente</H2>
      <P>
        Les présentes Conditions Générales de Vente (« CGV ») régissent la
        souscription aux abonnements payants du Service Arpentia. Elles
        complètent les CGU, dont l'acceptation est un préalable à toute
        souscription.
      </P>

      <H3>1. Offres et prix</H3>
      <P>
        Les abonnements, quotas de fiches et prix en vigueur sont décrits sur
        la page Tarifs. Les prix sont exprimés hors taxes, la TVA applicable
        étant ajoutée au moment du paiement selon les règles fiscales
        applicables à l'utilisateur. Les prix peuvent évoluer ; les abonnés en
        cours sont informés 30 jours à l'avance et peuvent résilier sans frais
        avant prise d'effet.
      </P>

      <H3>2. Paiement</H3>
      <P>
        Les paiements sont traités par Stripe Payments Europe Ltd. Arpentia ne
        collecte ni ne stocke aucune donnée de carte bancaire : seules les
        données nécessaires à la facturation (email, identifiant client Stripe)
        sont conservées. Les abonnements sont prélevés mensuellement par
        reconduction tacite à la date d'anniversaire de souscription.
      </P>

      <H3>3. Durée et résiliation</H3>
      <P>
        Les abonnements sont conclus pour une durée initiale d'un mois,
        renouvelable par tacite reconduction pour des périodes successives
        d'un mois. L'utilisateur peut résilier à tout moment depuis la page
        Paramètres ; la résiliation prend effet à la fin de la période en
        cours, sans remboursement au prorata.
      </P>

      <H3>4. Droit de rétractation</H3>
      <P>
        <strong>Utilisateurs professionnels (B2B)</strong> — Conformément à
        l'article L. 221-3 du Code de la consommation, les contrats conclus
        entre professionnels pour les besoins de leur activité ne bénéficient
        pas du droit de rétractation de 14 jours.
      </P>
      <P>
        <strong>Utilisateurs consommateurs (B2C)</strong> — L'utilisateur
        consommateur dispose d'un délai de rétractation de 14 jours à compter
        de la conclusion du contrat. Toutefois, en activant le Service
        (première génération de fiche) avant l'expiration de ce délai,
        l'utilisateur demande expressément l'exécution immédiate du Service et
        renonce à son droit de rétractation, conformément à l'article{" "}
        L. 221-28 1° du Code de la consommation.
      </P>

      <H3>5. Factures</H3>
      <P>
        Les factures sont émises automatiquement après chaque paiement et mises
        à disposition dans l'espace Paramètres de l'utilisateur. Elles sont
        conservées par Arpentia pendant la durée légale de conservation des
        pièces comptables, soit 10 ans (art. L. 123-22 du Code de commerce).
      </P>

      <H3>6. Suspension et résiliation par Arpentia</H3>
      <P>
        En cas de défaut de paiement après relance restée infructueuse, ou en
        cas de violation manifeste des CGU, Arpentia peut suspendre l'accès au
        Service ou résilier l'abonnement, après notification.
      </P>

      <H3>7. Service après-vente</H3>
      <P>
        Pour toute réclamation relative à la facturation ou au fonctionnement
        du Service, contactez{" "}
        <span className="font-mono text-[13px]">support@arpentia.fr</span>.
      </P>

      <H3>8. Médiation de la consommation</H3>
      <P>
        Conformément à l'article L. 616-1 du Code de la consommation,
        l'utilisateur consommateur peut recourir gratuitement à un médiateur
        de la consommation en cas de litige non résolu :{" "}
        <TODO>nom et coordonnées du médiateur agréé</TODO>.
      </P>

      <H3>9. Droit applicable</H3>
      <P>
        Les présentes CGV sont régies par le droit français. Tout litige entre
        professionnels relève des tribunaux compétents du ressort du siège
        social d'Arpentia.
      </P>
    </article>
  );
}
